import { t } from "@lingui/macro";
import { Envelope, PencilLine, Sparkle } from "@phosphor-icons/react";
import { Input, Textarea } from "@stylingcv/ui";
import { useEffect, useRef } from "react";
import { useState } from "react";

import { useResumeStore } from "@/client/stores/resume";
import { CoverLetterGenerationModal } from "@/client/pages/builder/_components/cover-letter-generation-modal";

type CoverLetterContentSectionProps = {
  autoOpenModal?: boolean;
};

export const CoverLetterContentSection = ({ autoOpenModal = false }: CoverLetterContentSectionProps) => {
  const setValue = useResumeStore((state) => state.setValue);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const hasAutoOpened = useRef(false);

  const coverLetterData = useResumeStore((state) => {
    try {
      return (state.resume.data as any).coverLetter || {
        body: "", recipientName: "", recipientTitle: "", companyName: "", jobTitle: "",
      };
    } catch {
      return { body: "", recipientName: "", recipientTitle: "", companyName: "", jobTitle: "" };
    }
  });

  useEffect(() => {
    if (autoOpenModal && !coverLetterData.body && !hasAutoOpened.current) {
      hasAutoOpened.current = true;
      const timer = setTimeout(() => setShowGenerateModal(true), 300);
      return () => clearTimeout(timer);
    }
  }, [autoOpenModal, coverLetterData.body]);

  const updateField = (field: string, value: string) => {
    try {
      setValue(`coverLetter.${field}` as any, value);
    } catch {
      // silent
    }
  };

  const handleGeneratedContent = (result: { content: string; jobTitle: string; company: string }) => {
    updateField("body", result.content);
    updateField("jobTitle", result.jobTitle);
    updateField("companyName", result.company);
  };

  const inputClass =
    "h-10 border-[var(--border,#e5e5e5)] bg-white text-[13px] text-[var(--fg-1,#171717)] placeholder:text-[var(--fg-4,#a3a3a3)] focus-visible:border-[var(--accent,#268599)] focus-visible:ring-1 focus-visible:ring-[var(--accent,#268599)]";

  return (
    <>
      <CoverLetterGenerationModal
        isOpen={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
        onGenerated={handleGeneratedContent}
      />

      {/* ── Header ── */}
      <header className="flex items-center gap-3 border-b border-[var(--border,#e5e5e5)] px-6 py-5">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-[rgba(38,133,153,0.10)]">
          <Envelope size={18} className="text-[var(--accent,#268599)]" weight="fill" />
        </div>
        <h2 className="flex-1 text-2xl font-bold leading-tight text-[var(--fg-1,#171717)]">
          {t`Cover Letter`}
        </h2>
        <button
          type="button"
          onClick={() => setShowGenerateModal(true)}
          className="flex items-center gap-1.5 rounded-[9px] bg-gradient-to-br from-[#155469] via-[#268599] to-[#E8885E] px-3.5 py-2 text-[12px] font-bold text-white shadow-[0_4px_14px_rgba(38,133,153,0.25)] transition-opacity hover:opacity-90"
        >
          <Sparkle size={13} weight="fill" />
          {t`Generate with AI`}
        </button>
      </header>

      {/* ── Body ── */}
      <div className="flex flex-col gap-6 p-6">

        {/* Empty state CTA */}
        {!coverLetterData.body && (
          <div className="flex flex-col items-center gap-4 rounded-2xl border border-dashed border-[rgba(38,133,153,0.25)] bg-[rgba(38,133,153,0.03)] px-6 py-8 text-center">
            <div className="flex size-14 items-center justify-center rounded-2xl bg-white shadow-md">
              <PencilLine size={28} className="text-[var(--accent,#268599)]" weight="duotone" />
            </div>
            <div>
              <p className="text-[15px] font-bold text-[var(--fg-1,#171717)]">{t`Write your cover letter`}</p>
              <p className="mt-1 text-[13px] text-[var(--fg-3,#737373)]">{t`Type it yourself or let AI generate it for you`}</p>
            </div>
            <button
              type="button"
              onClick={() => setShowGenerateModal(true)}
              className="flex items-center gap-2 rounded-[9px] bg-gradient-to-br from-[#155469] via-[#268599] to-[#E8885E] px-5 py-2.5 text-[13px] font-bold text-white shadow-[0_4px_14px_rgba(38,133,153,0.30)] transition-opacity hover:opacity-90"
            >
              <Sparkle size={15} weight="fill" />
              {t`Generate with AI`}
            </button>
          </div>
        )}

        {/* Recipient Details */}
        <div className="space-y-3">
          <h3 className="text-[11px] font-bold uppercase tracking-widest text-[var(--fg-4,#a3a3a3)]">
            {t`Recipient Details`}
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-[12px] font-semibold text-[var(--fg-3,#737373)]">
                {t`Recipient Name`}
              </label>
              <Input
                value={coverLetterData.recipientName || ""}
                onChange={(e) => updateField("recipientName", e.target.value)}
                placeholder={t`Hiring Manager`}
                className={inputClass}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[12px] font-semibold text-[var(--fg-3,#737373)]">
                {t`Recipient Title`}
              </label>
              <Input
                value={coverLetterData.recipientTitle || ""}
                onChange={(e) => updateField("recipientTitle", e.target.value)}
                placeholder={t`HR Director`}
                className={inputClass}
              />
            </div>
          </div>
        </div>

        {/* Cover Letter Body */}
        <div className="space-y-1.5">
          <h3 className="text-[11px] font-bold uppercase tracking-widest text-[var(--fg-4,#a3a3a3)]">
            {t`Content`}
          </h3>
          <Textarea
            value={coverLetterData.body || ""}
            onChange={(e) => updateField("body", e.target.value)}
            placeholder={t`Dear Hiring Manager,\n\nI am writing to express my interest in the position...`}
            rows={14}
            className="resize-y border-[var(--border,#e5e5e5)] text-[13px] text-[var(--fg-1,#171717)] focus-visible:border-[var(--accent,#268599)] focus-visible:ring-1 focus-visible:ring-[var(--accent,#268599)] focus-visible:outline-none"
          />
        </div>

        {/* Job Context pills */}
        {(coverLetterData.companyName || coverLetterData.jobTitle) && (
          <div className="flex flex-wrap gap-2">
            {coverLetterData.jobTitle && (
              <span className="rounded-full border border-[rgba(38,133,153,0.20)] bg-[rgba(38,133,153,0.06)] px-3 py-1 text-[12px] font-medium text-[var(--accent,#268599)]">
                {coverLetterData.jobTitle}
              </span>
            )}
            {coverLetterData.companyName && (
              <span className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-[12px] font-medium text-[var(--fg-2,#404040)]">
                {coverLetterData.companyName}
              </span>
            )}
          </div>
        )}
      </div>
    </>
  );
};
